import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Card, Row, Col, Statistic, Tag, Button, Progress } from 'antd';
import Table from 'components/Common/Table';
import {
  RiseOutlined,
  UserOutlined,
  ClockCircleOutlined,
  ExclamationCircleOutlined,
  ArrowRightOutlined
} from '@ant-design/icons';
import { loadTickets, loadTicketStatistics } from 'store/slices/SupportSlice';
import { formatDistanceToNow } from 'date-fns';

const SupportOverview = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const tickets = useSelector(state => state.support.tickets);
  const statistics = useSelector(state => state.support.ticketStatistics);
  const ticketsLoading = useSelector(state => state.support.ticketsLoading);

  useEffect(() => {
    // Load recent tickets and statistics
    dispatch(loadTickets({ page: 1, page_size: 5 }));
    dispatch(loadTicketStatistics());
  }, [dispatch]);

  const getStatusColor = (statusName) => {
    const name = statusName?.toLowerCase() || '';
    if (name.includes('closed') || name.includes('resolved')) return 'green';
    if (name.includes('progress')) return 'blue';
    if (name.includes('pending')) return 'orange';
    return 'cyan';
  };

  const getPriorityColor = (priority) => {
    const colors = {
      'CRITICAL': 'red',
      'HIGH': 'orange',
      'MEDIUM': 'blue',
      'LOW': 'default'
    };
    return colors[priority] || 'default';
  };


  const recentTicketsColumns = [
    {
      title: 'Ticket #',
      dataIndex: 'ticket_number',
      key: 'ticket_number',
      width: 130,
      render: (text) => <span style={{ fontFamily: 'monospace', fontWeight: 'bold' }}>{text}</span>
    },
    {
      title: 'Title',
      dataIndex: 'title',
      key: 'title',
      ellipsis: true,
      render: (text) => <strong>{text}</strong>
    },
    {
      title: 'Status',
      dataIndex: 'status_name',
      key: 'status',
      width: 120,
      render: (text) => <Tag color={getStatusColor(text)}>{text}</Tag>
    },
    {
      title: 'Priority',
      dataIndex: 'priority',
      key: 'priority',
      width: 100,
      render: (priority, record) => (
        <Tag color={getPriorityColor(priority)}>
          {record.priority_display || priority}
        </Tag>
      )
    },
    {
      title: 'Created',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 140,
      render: (date) => formatDistanceToNow(new Date(date), { addSuffix: true })
    }
  ];

  // Calculate SLA compliance rate
  const totalSLA = (statistics?.by_sla_status?.WITHIN_SLA || 0) +
                   (statistics?.by_sla_status?.APPROACHING_BREACH || 0) +
                   (statistics?.by_sla_status?.BREACHED || 0);
  const slaCompliance = totalSLA > 0
    ? ((statistics?.by_sla_status?.WITHIN_SLA || 0) / totalSLA * 100).toFixed(1)
    : 100;

  return (
    <div style={{ padding: '24px', minHeight: '100vh' }}>
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ margin: 0 }}>Support Overview</h1>
        <p style={{ color: '#666', margin: 0 }}>Real-time overview of your support operations</p>
      </div>

      {/* Key Metrics */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col span={6}>
          <Card>
            <Statistic
              title="Total Tickets"
              value={statistics?.total || 0}
              prefix={<RiseOutlined />}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Assigned to Me"
              value={statistics?.assigned_to_me || 0}
              prefix={<UserOutlined />}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Unassigned"
              value={statistics?.unassigned || 0}
              prefix={<ExclamationCircleOutlined />}
              valueStyle={{ color: '#faad14' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="SLA Breached"
              value={statistics?.by_sla_status?.BREACHED || 0}
              prefix={<ClockCircleOutlined />}
              valueStyle={{ color: '#f5222d' }}
            />
          </Card>
        </Col>
      </Row>

      {/* Status Distribution and SLA Compliance */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col span={12}>
          <Card title="Tickets by Status" extra={
            <Button type="link" onClick={() => navigate('/support/tickets')}>
              View All <ArrowRightOutlined />
            </Button>
          }>
            <div style={{ padding: '16px 0' }}>
              {statistics?.by_status && Object.entries(statistics.by_status).map(([status, count]) => (
                <div key={status} style={{ marginBottom: 12 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                    <Tag color={getStatusColor(status)}>{status}</Tag>
                    <strong>{count}</strong>
                  </div>
                  <Progress
                    percent={statistics.total > 0 ? (count / statistics.total * 100) : 0}
                    showInfo={false}
                    strokeColor={getStatusColor(status)}
                  />
                </div>
              ))}
            </div>
          </Card>
        </Col>

        <Col span={12}>
          <Card title="SLA Compliance" extra={
            <span style={{ fontSize: 20, fontWeight: 'bold', color: slaCompliance >= 90 ? '#52c41a' : '#faad14' }}>
              {slaCompliance}%
            </span>
          }>
            <Row gutter={16} style={{ marginBottom: 16 }}>
              <Col span={8}>
                <Statistic
                  title="Within SLA"
                  value={statistics?.by_sla_status?.WITHIN_SLA || 0}
                  valueStyle={{ color: '#52c41a', fontSize: 18 }}
                />
              </Col>
              <Col span={8}>
                <Statistic
                  title="Approaching"
                  value={statistics?.by_sla_status?.APPROACHING_BREACH || 0}
                  valueStyle={{ color: '#faad14', fontSize: 18 }}
                />
              </Col>
              <Col span={8}>
                <Statistic
                  title="Breached"
                  value={statistics?.by_sla_status?.BREACHED || 0}
                  valueStyle={{ color: '#f5222d', fontSize: 18 }}
                />
              </Col>
            </Row>
            <Progress
              percent={parseFloat(slaCompliance)}
              status={slaCompliance >= 90 ? 'success' : 'normal'}
              strokeColor={slaCompliance >= 90 ? '#52c41a' : '#faad14'}
            />
          </Card>
        </Col>
      </Row>

      {/* Priority Distribution */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col span={24}>
          <Card title="Tickets by Priority">
            <Row gutter={16}>
              <Col span={6}>
                <Card bordered={false} style={{ background: '#fff1f0' }}>
                  <Statistic
                    title="Critical"
                    value={statistics?.by_priority?.CRITICAL || 0}
                    valueStyle={{ color: '#f5222d' }}
                  />
                </Card>
              </Col>
              <Col span={6}>
                <Card bordered={false} style={{ background: '#fff7e6' }}>
                  <Statistic
                    title="High"
                    value={statistics?.by_priority?.HIGH || 0}
                    valueStyle={{ color: '#fa8c16' }}
                  />
                </Card>
              </Col>
              <Col span={6}>
                <Card bordered={false} style={{ background: '#e6f7ff' }}>
                  <Statistic
                    title="Medium"
                    value={statistics?.by_priority?.MEDIUM || 0}
                    valueStyle={{ color: '#1890ff' }}
                  />
                </Card>
              </Col>
              <Col span={6}>
                <Card bordered={false} style={{ background: '#f6ffed' }}>
                  <Statistic
                    title="Low"
                    value={statistics?.by_priority?.LOW || 0}
                    valueStyle={{ color: '#52c41a' }}
                  />
                </Card>
              </Col>
            </Row>
          </Card>
        </Col>
      </Row>

      {/* Recent Tickets */}
      <Row gutter={16}>
        <Col span={24}>
          <Card
            title="Recent Tickets"
            extra={
              <Button type="primary" onClick={() => navigate('/support/tickets')}>
                View All Tickets
              </Button>
            }
          >
            <Table
              columns={recentTicketsColumns}
              dataSource={tickets}
              loading={ticketsLoading}
              rowKey="id"
              pagination={false}
              onRow={(record) => ({
                onClick: () => navigate(`/support/tickets?viewId=${record.id}`),
                style: { cursor: 'pointer' }
              })}
            />
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default SupportOverview;
